import React from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';
import SkipToContent from '../components/SkipToContent';
import OtherStates from '../components/OtherStates';

interface ProfessionInfo {
  id: string;
  name: string;
  icon: string;
  credits: number;
  cycleYears: number;
  terminology: string;
  highlights: string[];
  stateBoard: string;
  boardUrl: string;
}

const ARLicenseRenewal: React.FC = () => {
  const professions: ProfessionInfo[] = [
    {
      id: 'ARCH',
      name: 'Architect',
      icon: '🏛️',
      credits: 12,
      cycleYears: 1,
      terminology: 'LU',
      highlights: [
        '12 LU annually',
        'Health, Safety & Welfare topics required',
        'AIA/CES or NCARB-approved providers',
        'Up to 4 LU self-directed study',
        'License renewed annually with ASBA'
      ],
      stateBoard: 'Arkansas State Board of Architects',
      boardUrl: 'https://www.arkansas.gov/arch/'
    },
    {
      id: 'PE',
      name: 'Professional Engineer',
      icon: '⚙️',
      credits: 15,
      cycleYears: 2,
      terminology: 'PDH',
      highlights: [
        '15 PDH biennial',
        'Min 1 PDH Ethics (mandatory)',
        'Technical engineering subjects required',
        'Max 8 PDH self-study allowed',
        'License renewed every 2 years'
      ],
      stateBoard: 'Arkansas State Board of Licensure for Professional Engineers and Professional Surveyors',
      boardUrl: 'https://pels.arkansas.gov/'
    },
    {
      id: 'CPA',
      name: 'Certified Public Accountant',
      icon: '💼',
      credits: 40,
      cycleYears: 1,
      terminology: 'CPE',
      highlights: [
        '40 CPE hours per calendar year',
        '2 hours Ethics (mandatory)',
        'Min 20 hours in accounting-related subjects',
        'NASBA-approved sponsors required',
        'Annual permit renewal: December 31'
      ],
      stateBoard: 'Arkansas State Board of Public Accountancy',
      boardUrl: 'https://www.arkansas.gov/asbpa/'
    },
    {
      id: 'ATT',
      name: 'Attorney',
      icon: '⚖️',
      credits: 12,
      cycleYears: 1,
      terminology: 'CLE',
      highlights: [
        '12 CLE hours per year',
        '1 hour Ethics/Professional Conduct (mandatory)',
        'Max 6 self-study hours annually',
        'Carry-over of excess hours allowed',
        'Annual reporting deadline: December 31'
      ],
      stateBoard: 'Arkansas CLE Board',
      boardUrl: 'https://courts.arkansas.gov/administration/cle-board'
    },
    {
      id: 'RN',
      name: 'Registered Nurse',
      icon: '⚕️',
      credits: 15,
      cycleYears: 2,
      terminology: 'contact hours',
      highlights: [
        '15 contact hours biennial',
        'Nursing-related topics required',
        'ANCC or state-approved CE programs',
        'Up to 7 hours self-study allowed',
        'License renewed every 2 years'
      ],
      stateBoard: 'Arkansas State Board of Nursing',
      boardUrl: 'https://www.arsbn.org/'
    },
    {
      id: 'PHARM',
      name: 'Pharmacist',
      icon: '💊',
      credits: 15,
      cycleYears: 1,
      terminology: 'CE',
      highlights: [
        '15 CE hours per year',
        '1 hour Arkansas pharmacy law (mandatory)',
        '1 hour patient safety/medication errors',
        'ACPE-accredited programs required',
        'License renewed annually'
      ],
      stateBoard: 'Arkansas State Board of Pharmacy',
      boardUrl: 'https://www.arkansas.gov/pharmacy/'
    }
  ];

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Arkansas Professional License Renewal Requirements",
      "url": "https://cectrack.com/ar-license-renewal",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://cectrack.com" },
          { "@type": "ListItem", "position": 2, "name": "Arkansas License Renewal", "item": "https://cectrack.com/ar-license-renewal" }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How many CE hours do Arkansas architects need each year?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Arkansas architects must complete 12 learning units (LU) annually. Requirements include Health, Safety & Welfare topics from AIA/CES or NCARB-approved providers. Up to 4 LU of self-directed study are allowed."
          }
        },
        {
          "@type": "Question",
          "name": "Do Arkansas professional engineers need continuing education?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Arkansas professional engineers must complete 15 PDH every 2 years, including at least 1 PDH in Ethics. Technical engineering subjects are required and up to 8 PDH of self-study are allowed."
          }
        },
        {
          "@type": "Question",
          "name": "What are the CLE requirements for Arkansas attorneys?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Arkansas attorneys must complete 12 CLE hours per year, including 1 hour of Ethics/Professional Conduct. Up to 6 hours of self-study are allowed annually, and the reporting deadline is December 31."
          }
        },
        {
          "@type": "Question",
          "name": "Do Arkansas nurses need continuing education for license renewal?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Arkansas registered nurses must complete 15 contact hours every 2 years. Courses must be nursing-related from ANCC or state-approved CE providers. Up to 7 hours of self-study are allowed per renewal period."
          }
        }
      ]
    }
  ];

  return (
    <>
      <SkipToContent />
      <SEOHelmet
        title="Arkansas Professional License Renewal | CE Requirements"
        description="Complete guide to Arkansas professional license renewal requirements. Track CE credits for Architects, Engineers, CPAs, Attorneys, Nurses, and Pharmacists in Arkansas."
        keywords="Arkansas license renewal, AR CE requirements, Arkansas continuing education, Arkansas professional licenses, ASBA, Arkansas CPA CPE, Arkansas CLE, Arkansas nursing CE"
        canonical="https://cectrack.com/ar-license-renewal"
        ogImage="https://cectrack.com/og-image.png"
        structuredData={structuredData}
      />

      <section
        aria-label="Page header"
        style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', color: 'white', padding: '80px 0 60px', position: 'relative', overflow: 'hidden' }}
      >
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")", opacity: 0.4 }} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '1.5rem', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                Arkansas Professional License Renewal
              </h1>
              <p style={{ fontSize: '1.3rem', marginBottom: '2rem', opacity: 0.95 }}>
                Track CE requirements across all your Arkansas professional licenses in one place.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link to="/register" className="btn btn-light btn-lg px-5 py-3" style={{ fontWeight: '600', borderRadius: '8px' }}>Start Tracking Free</Link>
                <a href="#professions" className="btn btn-outline-light btn-lg px-5 py-3" style={{ fontWeight: '600', borderRadius: '8px' }}>View Requirements</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section aria-label="Statistics" style={{ padding: '60px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row g-4 text-center">
            <div className="col-md-3"><div className="p-4"><div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>6</div><div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Professions Supported</div></div></div>
            <div className="col-md-3"><div className="p-4"><div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>50+</div><div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Category Requirements</div></div></div>
            <div className="col-md-3"><div className="p-4"><div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>100%</div><div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Board Compliant</div></div></div>
            <div className="col-md-3"><div className="p-4"><div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>24/7</div><div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Access Anywhere</div></div></div>
          </div>
        </div>
      </section>

      <main id="main-content">
        <section id="professions" aria-labelledby="professions-heading" style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2 id="professions-heading" style={{ fontSize: '2.5rem', fontWeight: '700' }}>Arkansas CE Requirements by Profession</h2>
            </div>
            <div className="row g-4">
              {professions.map((prof) => (
                <div key={prof.id} className="col-md-6 col-lg-4">
                  <div
                    className="card h-100 border-0 shadow-sm"
                    style={{ transition: 'all 0.3s ease', cursor: 'pointer' }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'; }}
                  >
                    <div className="card-body p-4">
                      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{prof.icon}</div>
                      <h5 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1rem' }}>{prof.name}</h5>
                      <div style={{ backgroundColor: '#e7f3ff', padding: '12px 16px', borderRadius: '8px', marginBottom: '1rem' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e3a8a' }}>
                          {prof.credits > 0 ? `${prof.credits} ${prof.terminology}` : 'No State Mandate'}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                          {prof.cycleYears === 1 ? 'Annual' : prof.cycleYears === 2 ? 'Biennial' : 'Triennial'} Renewal
                        </div>
                      </div>
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6c757d', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Key Requirements:</div>
                        <ul style={{ fontSize: '0.9rem', margin: 0, paddingLeft: '1.2rem' }}>
                          {prof.highlights.map((h, i) => <li key={i} style={{ marginBottom: '0.3rem' }}>{h}</li>)}
                        </ul>
                      </div>
                      <div style={{ paddingTop: '1rem', borderTop: '1px solid #e9ecef', fontSize: '0.85rem', color: '#6c757d' }}>
                        <strong>{prof.stateBoard}</strong><br />
                        <a href={prof.boardUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none' }}>Visit State Board →</a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6 mb-4 mb-lg-0">
                <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>Simplify Your Arkansas License Management</h2>
                <p style={{ fontSize: '1.1rem', color: '#6c757d', marginBottom: '2rem' }}>CEC Track automatically tracks your requirements, calculates your progress, and sends timely reminders so you never miss a renewal deadline.</p>
                <ul style={{ fontSize: '1.1rem', listStyle: 'none', padding: 0 }}>
                  {[
                    'Automated deadline tracking for all Arkansas professions',
                    'Upload certificates and match to requirements',
                    'Real-time progress reports by category',
                    'Multi-state support for professionals licensed in multiple states',
                    'Secure cloud storage for all documents',
                    'Email notifications for upcoming deadlines'
                  ].map((f, i) => (
                    <li key={i} style={{ marginBottom: '1rem', paddingLeft: '2rem', position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 0, color: '#10b981', fontSize: '1.5rem' }}>✓</span>{f}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="col-lg-6">
                <div className="p-5 text-center" style={{ backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}>
                  <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📱</div>
                  <h3 style={{ fontSize: '1.8rem', fontWeight: '700', marginBottom: '1rem' }}>Start Tracking Today</h3>
                  <p style={{ color: '#6c757d', marginBottom: '2rem' }}>Join professionals across Arkansas who trust CEC Track to manage their continuing education.</p>
                  <Link to="/register" className="btn btn-primary btn-lg px-5 py-3" style={{ fontWeight: '600', background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', border: 'none', borderRadius: '8px' }}>Create Free Account</Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section aria-labelledby="resources-heading" style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2 id="resources-heading" style={{ fontSize: '2.5rem', fontWeight: '700' }}>Arkansas State Resources</h2>
            </div>
            <div className="row g-4">
              <div className="col-md-6">
                <article className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>Arkansas State Board of Public Accountancy</h3>
                    <p style={{ color: '#6c757d', marginBottom: '1rem' }}>Regulates CPA licensing and CPE requirements in Arkansas.</p>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Phone:</strong> <a href="tel:+15016824476">(501) 682-1520</a></div>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Website:</strong> <a href="https://www.arkansas.gov/asbpa/" target="_blank" rel="noopener noreferrer">arkansas.gov/asbpa</a></div>
                    <div style={{ marginBottom: '1rem' }}><strong>Office:</strong> 101 E Capitol Ave, Suite 450, Little Rock, AR 72201</div>
                    <a href="https://www.arkansas.gov/asbpa/" target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary">View CPA Requirements</a>
                  </div>
                </article>
              </div>
              <div className="col-md-6">
                <article className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>Arkansas CLE Board</h3>
                    <p style={{ color: '#6c757d', marginBottom: '1rem' }}>Administers continuing legal education requirements for Arkansas attorneys.</p>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Phone:</strong> <a href="tel:+15016829400">(501) 374-1855</a></div>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Website:</strong> <a href="https://courts.arkansas.gov/administration/cle-board" target="_blank" rel="noopener noreferrer">courts.arkansas.gov/cle-board</a></div>
                    <div style={{ marginBottom: '1rem' }}><strong>Office:</strong> 625 Marshall Street, Justice Bldg, Little Rock, AR 72201</div>
                    <a href="https://courts.arkansas.gov/administration/cle-board" target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary">View CLE Requirements</a>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </section>

        <OtherStates currentState="AR" />

        <section aria-label="Call to action" style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', color: 'white', padding: '80px 0' }}>
          <div className="container text-center">
            <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>Ready to Simplify Your Arkansas License Renewal?</h2>
            <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: 0.95 }}>Join professionals across Arkansas who never worry about missing CE requirements</p>
            <Link to="/register" className="btn btn-light btn-lg px-5 py-3" style={{ fontWeight: '600', borderRadius: '8px' }}>Get Started Free</Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default ARLicenseRenewal;
